import { DocumentModel } from '../../../../core/models/document-model';
import { CommentModel } from '../../../../core/models/comment-model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { CheckService } from '../../../../core/checks/check';
import { DocumentService } from '../../../../core/documents/document';
import { CommentService } from '../../../../core/comments/comment';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {forkJoin, from, mergeMap, of, pipe, retry, toArray} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import { tapResponse } from '@ngrx/operators';


type DocumentCommentGroup = {
  document: DocumentModel;
  comments: CommentModel[];
  loadFailed: boolean;
};

type documentState = {
  documents: DocumentModel[];
  documentComments: DocumentCommentGroup[];
  documentCommentsLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: documentState = {
  documents: [],
  documentComments: [],
  documentCommentsLoaded: false,
  isLoading: false,
  error: null
}

export const PendingCheckDocumentStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({

    documentsById: computed(() =>
      new Map(store.documents().map(d => [d.guid, d]))
    ),

    documentsForCheck: computed(() => store.documents()),

    failedDocumentCommentIds: computed(() =>
      new Set(store.documentComments().filter(g => g.loadFailed).map(g => g.document.guid))
    ),

    hasFailedDocumentComments: computed(() =>
      store.documentComments().some(g => g.loadFailed)
    ),

  })),

  withMethods((
    store,
    checkService = inject(CheckService),
    documentService = inject(DocumentService),
    commentService = inject(CommentService)
  ) => ({

    loadDocumentsByCheckId: rxMethod<string | null>(
      pipe(
        tap(() => patchState(store, { isLoading: true, error: null })),
        switchMap((checkId) => {
          if (!checkId) {
            patchState(store, { documents: [], documentComments: [], isLoading: false });
            return of(null);
          }

          return checkService.getCheckDocumentsApi(checkId).pipe(
            tapResponse({
              next: (documents) => patchState(store, { documents, isLoading: false }),
              error: (err: any) => {
                patchState(store, { error: err.message, isLoading: false });
                console.error('Fehler beim Laden der Dokumente: ', err);
              }
            })
          )
        })
      )
    ),

    refreshDocuments(currentCheckId: string | null) {
      if (currentCheckId) {
        this.loadDocumentsByCheckId(currentCheckId);
      }
    },

    loadCommentsForDocuments: rxMethod<DocumentModel[]>(
      pipe(
        switchMap((docs) => {
          const uniqueDocs = Array.from(
            new Map(docs.map(d => [d.guid, d])).values()
          );

          if (uniqueDocs.length !== docs.length) {
            console.warn(
              `[DocumentStore] ${docs.length - uniqueDocs.length} doppelte Dokument-GUID(s) entfernt.`
            );
          }

          if (docs.length === 0) {
            patchState(store, { documentComments: [] });
            return of([] as DocumentCommentGroup[]);
          }

          const requests = docs.map(doc =>
            documentService.getDocumentCommentsApi(doc.guid).pipe(
              map(dtos => dtos.filter(dto => dto.severity !== 'HINT')),
              map(dtos => dtos.map(dto => commentService.mapComment(dto, new Map(), new Map()))),
              map(comments => ({ document: doc, comments })),
              catchError((err) => {
                console.error(`Fehler beim Laden der Kommentare für Dokument ${doc.guid}: `, err);
                return of({ document: doc, comments: [] });
              }),
            )
          );

          return from(uniqueDocs).pipe(
            mergeMap(doc =>
                documentService.getDocumentCommentsApi(doc.guid).pipe(
                  retry({ count: 2, delay: 500 }),
                  map(dtos => dtos.filter(dto => dto.severity !== 'HINT')),
                  map(dtos => dtos.map(dto => commentService.mapComment(dto, new Map(), new Map()))),
                  map(comments => ({ document: doc, comments, loadFailed: false })),
                  catchError((err) => {
                    console.error(
                      `[DocumentStore] Kommentare für Dokument ${doc.guid} (${doc.displayName}) konnten nicht geladen werden:`,
                      err
                    );
                    return of({ document: doc, comments: [], loadFailed: true });
                  }),
                ),
              4 // max. 4 parallele Requests
            ),
            toArray(),
            tapResponse({
              next: (documentComments) => patchState(store, {
                documentComments,
                documentCommentsLoaded: true,
              }),
              error: (err: any) => {
                patchState(store, { error: err.message });
                console.error('Fehler beim Laden der Dokument-Kommentare: ', err);
              }
            })
          );
        })
      )
    ),

  }))

);
